/**
 * Pure (no-React) model for the Assets tab: `fim-asset-instance` entries are
 * the ONLY inventory source. Each instance carries its identity/lifecycle and
 * the absorbed fill result (`instance.fill`, llm-service FilledSlot payloads
 * verbatim) — there are no separate fill entries to join and no legacy lanes.
 */
import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'

import type { FilledSlot } from './templateTypes'
import type {
  AssetInstanceFillStatus,
  FimAssetInstanceData,
  FimAssetInstanceFill,
} from './types'
import type { SubmitReviewRequest } from './useFimService'

export interface AssetInventoryEntry {
  id: string
  data: unknown
  tags?: Record<string, unknown>
}

export const FILL_STATUS_LABEL: Record<
  AssetInstanceFillStatus,
  MessageDescriptor
> = {
  missing_type: msg`Missing type`,
  no_template: msg`No template`,
  queued: msg`Fill queued`,
  running: msg`Filling`,
  needs_review: msg`Needs review`,
  complete: msg`Complete`,
  failed: msg`Fill failed`,
  stale: msg`Fill stale`,
}

/**
 * The label for a fill status, verbatim fallback included: a status this build
 * doesn't know (services deploy independently and own the lifecycle) renders
 * as itself — never the literal "undefined".
 */
export const fillStatusLabel = (status: AssetInstanceFillStatus): string => {
  const label = (
    FILL_STATUS_LABEL as Partial<Record<string, MessageDescriptor>>
  )[status]
  return label ? i18n._(label) : status
}

export const isAssetInstance = (data: unknown): data is FimAssetInstanceData =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as { subject_uid?: unknown }).subject_uid === 'string' &&
  typeof (data as { canonical_name?: unknown }).canonical_name === 'string' &&
  typeof (data as { fill_status?: unknown }).fill_status === 'string'

/** One Assets-list row: the instance plus its list-level derived fields. */
export interface AssetInstanceSummary {
  /** Rolodex entry id (`fim-asset-instance-<subject_uid>`). */
  slug: string
  name: string
  system: string | null
  /** Slots bound by the fill. */
  filledCount: number
  /** Slots the fill left unresolved. */
  gapCount: number
  /** Total slots in the fill (0 until a fill exists). */
  totalCount: number
  state: AssetInstanceFillStatus
  subjectUid: string
  assetType: string | null
  fillError: string | null
  /** Any PI-tag slot bound to at least one historian tag. */
  hasProcessData: boolean
  /** Any document slot bound to at least one document. */
  hasDocuments: boolean
  instance: FimAssetInstanceData
}

export const slotCounts = (
  fill: FimAssetInstanceFill | null | undefined
): { filledCount: number; gapCount: number; totalCount: number } => {
  // A summary-shaped fill carries the tally instead of the slots themselves;
  // the list cell renders the same either way.
  if (fill && fill.slots === undefined && fill.slots_total !== undefined) {
    return {
      filledCount: fill.slots_filled ?? 0,
      gapCount: fill.slots_gaps ?? 0,
      totalCount: fill.slots_total,
    }
  }
  let filledCount = 0
  let gapCount = 0
  const slots = fill?.slots ?? []
  for (const slot of slots) {
    if (slot.status === 'bound') filledCount += 1
    else if (slot.status === 'unresolved') gapCount += 1
  }
  return { filledCount, gapCount, totalCount: slots.length }
}

const hasBoundFamily = (
  fill: FimAssetInstanceFill | null | undefined,
  family: FilledSlot['slot_family']
): boolean =>
  (fill?.slots ?? []).some(
    (slot) => slot.slot_family === family && slot.bound_refs.length > 0
  )

/**
 * Builds the Assets inventory from `fim-asset-instance` entries alone. The
 * lifecycle (`fill_status`) is owned by fim-service/llm-service and passes
 * straight through — no client-side re-derivation.
 */
export const buildAssetInventory = (
  instanceEntries: readonly AssetInventoryEntry[]
): AssetInstanceSummary[] =>
  instanceEntries
    .flatMap<AssetInstanceSummary>((entry) => {
      if (!isAssetInstance(entry.data)) return []
      const instance = entry.data
      return [
        {
          slug: entry.id,
          name: instance.canonical_name,
          system: instance.subsystem_id ?? null,
          ...slotCounts(instance.fill),
          state: instance.fill_status,
          subjectUid: instance.subject_uid,
          assetType: instance.type ?? null,
          fillError: instance.fill_error ?? null,
          hasProcessData: hasBoundFamily(instance.fill, 'pi_tag'),
          hasDocuments: hasBoundFamily(instance.fill, 'document'),
          instance,
        },
      ]
    })
    .sort((left, right) => left.name.localeCompare(right.name))

// ---------------------------------------------------------------------------
// List filters + sort (pure model behind the Filters menu)
// ---------------------------------------------------------------------------

/** Facet value standing in for a null `assetType`; rendered as "Untyped". */
export const UNTYPED_ASSET_TYPE = '(untyped)'

export type AssetSortMode = 'name' | 'updated-desc'

export const ASSET_SORT_LABEL: Record<AssetSortMode, MessageDescriptor> = {
  name: msg`Name A–Z`,
  'updated-desc': msg`Recently updated`,
}

export interface AssetFilters {
  /** Asset types to show (`UNTYPED_ASSET_TYPE` for null); empty = all. */
  types: string[]
  /** Fill statuses to show; empty = all. */
  states: AssetInstanceFillStatus[]
  processData: 'any' | 'present' | 'absent'
  documents: 'any' | 'present' | 'absent'
  sort: AssetSortMode
}

/** No filtering: every asset is shown, in the default name order. */
export const DEFAULT_ASSET_FILTERS: AssetFilters = {
  types: [],
  states: [],
  processData: 'any',
  documents: 'any',
  sort: 'name',
}

/** How many facets (plus a non-default sort) are narrowing the list. */
export const activeAssetFilterCount = (f: AssetFilters): number =>
  (f.types.length > 0 ? 1 : 0) +
  (f.states.length > 0 ? 1 : 0) +
  (f.processData === 'any' ? 0 : 1) +
  (f.documents === 'any' ? 0 : 1) +
  (f.sort === DEFAULT_ASSET_FILTERS.sort ? 0 : 1)

/** Whether a single asset row passes the current filter selections. */
export const assetMatchesFilters = (
  asset: AssetInstanceSummary,
  f: AssetFilters
): boolean => {
  if (
    f.types.length > 0 &&
    !f.types.includes(asset.assetType ?? UNTYPED_ASSET_TYPE)
  ) {
    return false
  }
  if (f.states.length > 0 && !f.states.includes(asset.state)) return false
  if (f.processData === 'present' && !asset.hasProcessData) return false
  if (f.processData === 'absent' && asset.hasProcessData) return false
  if (f.documents === 'present' && !asset.hasDocuments) return false
  if (f.documents === 'absent' && asset.hasDocuments) return false
  return true
}

const filledAtMs = (asset: AssetInstanceSummary): number | null => {
  const raw = asset.instance.fill?.filled_at
  const ms = raw ? Date.parse(raw) : NaN
  return Number.isNaN(ms) ? null : ms
}

const compareAssetsBy: Record<
  AssetSortMode,
  (left: AssetInstanceSummary, right: AssetInstanceSummary) => number
> = {
  name: (left, right) => left.name.localeCompare(right.name),
  'updated-desc': (left, right) => {
    const leftMs = filledAtMs(left)
    const rightMs = filledAtMs(right)
    if (leftMs !== null && rightMs !== null) {
      return rightMs - leftMs || left.name.localeCompare(right.name)
    }
    if (leftMs !== null) return -1
    if (rightMs !== null) return 1
    return left.name.localeCompare(right.name)
  },
}

export const sortAssets = (
  assets: readonly AssetInstanceSummary[],
  mode: AssetSortMode
): AssetInstanceSummary[] => [...assets].sort(compareAssetsBy[mode])

// ---------------------------------------------------------------------------
// Fill-derived viewer inputs (documents + process data)
// ---------------------------------------------------------------------------

/** A real, openable document id is `DOC-` + a hex hash; synthetic refs skip. */
const OPENABLE_DOC_ID = /^DOC-[a-f0-9]{12,}$/i

/** Whether a document-slot ref can actually open in the viewer. */
export const isOpenableDocId = (ref: string | null | undefined): boolean =>
  OPENABLE_DOC_ID.test((ref ?? '').trim())

export interface AssetDocRef {
  doc_id: string
  title?: string
  isPid: boolean
}

const PID_REGEX =
  /p\s*&\s*id|piping\s*(?:and|&)?\s*instrument|(?:^|[^a-z])pid(?:[^a-z]|$)/i

/**
 * The unique openable documents bound by the fill's document slots, labeled
 * with the slot's `bound_names` (index-aligned filenames). P&IDs sort first —
 * the best sheet for an asset.
 */
export const collectDocumentRefs = (
  fill: FimAssetInstanceFill | null | undefined
): AssetDocRef[] => {
  const refs: AssetDocRef[] = []
  const seen = new Set<string>()
  for (const slot of fill?.slots ?? []) {
    if (slot.slot_family !== 'document') continue
    const isPidSlot = PID_REGEX.test(
      `${slot.label ?? slot.key} ${slot.doc_kind ?? ''}`
    )
    slot.bound_refs.forEach((ref, index) => {
      const id = ref.trim()
      if (!OPENABLE_DOC_ID.test(id) || seen.has(id)) return
      seen.add(id)
      const title = slot.bound_names?.[index]?.trim() || undefined
      refs.push({
        doc_id: id,
        title,
        isPid: isPidSlot || PID_REGEX.test(title ?? ''),
      })
    })
  }
  return [...refs.filter((r) => r.isPid), ...refs.filter((r) => !r.isPid)]
}

/**
 * Each PI-tag slot's bound historian tags, keyed by slot key — the per-slot
 * "add to process data" toggle input. Callers resolve tags to chartable
 * datapoint ids via the site datapoints query.
 */
export const piTagsBySlot = (
  fill: FimAssetInstanceFill | null | undefined
): Record<string, string[]> => {
  const map: Record<string, string[]> = {}
  for (const slot of fill?.slots ?? []) {
    if (slot.slot_family !== 'pi_tag') continue
    const tags: string[] = []
    for (const ref of slot.bound_refs) {
      const tag = ref.trim()
      if (tag && !tags.includes(tag)) tags.push(tag)
    }
    if (tags.length > 0) map[slot.key] = tags
  }
  return map
}

/**
 * The first actionable ref of a slot — the review target for Confirm/Reject.
 * `exclude` skips refs already adjudicated this session (a rejected candidate
 * must not become the next verdict's target while it is still rendered).
 */
export const slotReviewRef = (
  slot: FilledSlot,
  exclude?: ReadonlySet<string>
): string | undefined => {
  const usable = (ref: string | null | undefined): string | undefined => {
    const trimmed = ref?.trim()
    return trimmed && !exclude?.has(trimmed) ? trimmed : undefined
  }
  for (const cand of slot.candidates ?? []) {
    const ref = usable(cand.ref)
    if (ref) return ref
  }
  for (const bound of slot.bound_refs) {
    const ref = usable(bound)
    if (ref) return ref
  }
  return undefined
}

/**
 * The slot Confirm/Reject request (fill-ownership protocol v2 §8): the BATCH
 * `decisions` form, because fim-service's single-decision form drops the
 * binding fields (review_kind/template_id/slot_key/to_ref) — a slot verdict
 * posted that way degrades into an identity review with fusion side effects.
 * Binding reviews are audit-only: `review_kind` doc_binding/tag_binding never
 * emits identity constraints and `apply: 'none'` never touches the
 * projections. `alias_keys` is inert audit context for binding kinds, but the
 * service validates it non-empty for every decision, so the instance's
 * aliases ride along (falling back to its canonical name).
 */
export const buildSlotReviewRequest = (
  instance: FimAssetInstanceData,
  slot: FilledSlot,
  verdict: 'accept' | 'reject',
  reviewer: string,
  site: string,
  toRef: string
): SubmitReviewRequest => ({
  decisions: [
    {
      action: verdict,
      review_kind:
        slot.slot_family === 'document' ? 'doc_binding' : 'tag_binding',
      template_id: instance.template_id ?? undefined,
      slot_key: slot.key,
      to_ref: toRef,
      target_subject_uid: instance.subject_uid,
      alias_keys: instance.aliases?.length
        ? instance.aliases
        : [instance.canonical_name],
      reason: slot.why ?? undefined,
    },
  ],
  apply: 'none',
  reviewer,
  site,
})

/** One consolidated row of a PI-tag slot — a historian ref with every
 * instrument identity that resolves to it, or an unhistorised instrument. */
export interface SlotBindingRow {
  /** Historian PI tag; null when the sensor isn't historised. */
  ref: string | null
  /** Instrument identities merged for this ref — evidence instrument tags and
   * candidate subject identities, deduped (a sensor's naming-convention twins
   * collapse onto one row). */
  instrumentIds: string[]
  /** True while the ref is an unadjudicated review candidate. */
  candidate: boolean
  /** The candidate's agent reason, when one exists (tooltip context). */
  reason?: string
}

/** One committed Confirm/Reject this session: the verdict and its target ref. */
export interface SlotVerdict {
  verdict: 'accept' | 'reject'
  ref: string
}

/**
 * Applies committed slot verdicts to a fill, mirroring fim-service's
 * `mirror_binding_adjudication` (00-contracts ADDENDUM §11). Verdicts are
 * recorded only AFTER their review commits, so this overlay bridges the gap
 * between the committed review and the next refetch — it is not pre-commit
 * optimism. Accept moves the ref into
 * `bound_refs` (consuming the matching candidate) and binds the slot; reject
 * removes the ref from `bound_refs` (with its aligned name/evidence) and the
 * slot degrades to review/bound/unresolved. Rejected CANDIDATES are kept in
 * the list — the UI strikes them through until the refetch drops them — but
 * never count toward the slot status.
 */
export const applySlotVerdicts = (
  fill: FimAssetInstanceFill,
  verdicts: Record<string, SlotVerdict[]>
): FimAssetInstanceFill => {
  if (Object.keys(verdicts).length === 0) return fill
  const slots = (fill.slots ?? []).map((slot) => {
    const slotVerdicts = verdicts[slot.key]
    if (!slotVerdicts?.length) return slot
    let next = slot
    for (const { verdict, ref } of slotVerdicts) {
      if (verdict === 'accept') {
        next = {
          ...next,
          bound_refs: next.bound_refs.includes(ref)
            ? next.bound_refs
            : [...next.bound_refs, ref],
          candidates: (next.candidates ?? []).filter(
            (cand) => cand.ref?.trim() !== ref
          ),
          status: 'bound',
        }
        continue
      }
      const index = next.bound_refs.indexOf(ref)
      const boundRefs = next.bound_refs.filter((bound) => bound !== ref)
      const liveCandidates = (next.candidates ?? []).filter((cand) => {
        const candRef = cand.ref?.trim()
        return !candRef || !slotVerdicts.some((v) => v.ref === candRef)
      })
      next = {
        ...next,
        bound_refs: boundRefs,
        bound_names:
          index >= 0 && next.bound_names
            ? next.bound_names.filter((_, nameIndex) => nameIndex !== index)
            : next.bound_names,
        pi_tag_evidence: next.pi_tag_evidence?.filter(
          (evidence) => evidence.ref?.trim() !== ref
        ),
        status:
          liveCandidates.length > 0
            ? 'review'
            : boundRefs.length > 0
              ? 'bound'
              : 'unresolved',
      }
    }
    return next
  })
  return { ...fill, slots }
}

/** Every historian ref a PI-tag slot mentions (bound, evidence, candidate) —
 * the pool the per-row chart toggles resolve datapoints for. */
export const slotPiTagRefs = (slot: FilledSlot): string[] => {
  if (slot.slot_family !== 'pi_tag') return []
  const refs: string[] = []
  const push = (ref: string | null | undefined) => {
    const trimmed = ref?.trim()
    if (trimmed && !refs.includes(trimmed)) refs.push(trimmed)
  }
  slot.bound_refs.forEach(push)
  slot.pi_tag_evidence?.forEach((evidence) => push(evidence.ref))
  slot.candidates?.forEach((cand) => push(cand.ref))
  return refs
}

/** A subject uid's displayable identity: the tag after the `site$` prefix. */
const displayIdentity = (raw: string | null | undefined): string | null => {
  const trimmed = raw?.trim()
  if (!trimmed) return null
  const dollar = trimmed.lastIndexOf('$')
  return dollar >= 0 ? trimmed.slice(dollar + 1) : trimmed
}

/**
 * Consolidated per-ref rows for a PI-tag slot. The raw fill mentions the same
 * facts up to three times — `pi_tag_evidence` (ref ↔ instrument pairing),
 * `candidates` (ref ↔ subject identity, repeated per naming-convention
 * variant of one physical sensor), and `instrument_ids` (every sensor,
 * historised or not). One row per historian ref merges all identities that
 * resolve to it; instruments no row carries under ANY identity render
 * unhistorised — so a sensor's naming twin surfaced via candidates folds away
 * instead of doubling the list.
 */
export const slotBindingRows = (slot: FilledSlot): SlotBindingRow[] => {
  const rows: SlotBindingRow[] = []
  const byRef = new Map<string, SlotBindingRow>()
  const rowFor = (ref: string): SlotBindingRow => {
    let row = byRef.get(ref)
    if (!row) {
      row = { ref, instrumentIds: [], candidate: false }
      byRef.set(ref, row)
      rows.push(row)
    }
    return row
  }
  const addIdentity = (row: SlotBindingRow, raw: string | null | undefined) => {
    const identity = displayIdentity(raw)
    if (identity && !row.instrumentIds.includes(identity)) {
      row.instrumentIds.push(identity)
    }
  }

  const reflessInstruments: (string | null | undefined)[] = []
  for (const evidence of slot.pi_tag_evidence ?? []) {
    const ref = evidence.ref?.trim()
    if (ref) addIdentity(rowFor(ref), evidence.instrument_id)
    else reflessInstruments.push(evidence.instrument_id)
  }
  for (const raw of slot.bound_refs) {
    const ref = raw.trim()
    if (ref) rowFor(ref)
  }
  for (const cand of slot.candidates ?? []) {
    const ref = cand.ref?.trim()
    if (!ref) continue
    const row = rowFor(ref)
    row.candidate = true
    if (!row.reason && cand.reason?.trim()) row.reason = cand.reason.trim()
    addIdentity(row, cand.subject_uid)
  }
  const known = new Set(rows.flatMap((row) => row.instrumentIds))
  for (const raw of [...(slot.instrument_ids ?? []), ...reflessInstruments]) {
    const identity = displayIdentity(raw)
    if (identity && !known.has(identity)) {
      known.add(identity)
      rows.push({ ref: null, instrumentIds: [identity], candidate: false })
    }
  }
  return rows
}
